import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Shield, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle, Users, Heart } from 'lucide-react';
import { API_URL } from '@/services/apiClient';

interface ScoreBreakdown {
  score: number;
  label: string;
  weight: number;
}

interface SafetyScoreData {
  overall: number;
  trend: number;
  breakdown: {
    resolution: ScoreBreakdown;
    critical: ScoreBreakdown;
    coverage: ScoreBreakdown;
    wellness: ScoreBreakdown;
  };
  stats: {
    totalAlerts: number;
    resolvedAlerts: number;
    criticalAlerts: number;
    openCritical: number;
    safeStudents: number;
    totalStudents: number;
  };
}

function ScoreGauge({ score, size = 160 }: { score: number; size?: number }) {
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  // 270° arc — gap of 90° sits at the bottom
  const arcFraction = 0.75;
  const trackLength = circumference * arcFraction;
  const scoreLength = trackLength * Math.max(0, Math.min(100, score)) / 100;
  const color = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';
  // Rotate so the arc starts at bottom-left (135° from 3 o'clock = 225° from top)
  const rotation = 135;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        {/* Track */}
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="#e2e8f0" strokeWidth={strokeWidth}
          strokeDasharray={`${trackLength} ${circumference}`}
          strokeLinecap="round"
          style={{ transform: `rotate(${rotation}deg)`, transformOrigin: '50% 50%' }} />
        {/* Score */}
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={`${scoreLength} ${circumference}`}
          strokeLinecap="round"
          style={{ transform: `rotate(${rotation}deg)`, transformOrigin: '50% 50%', transition: 'stroke-dasharray 0.8s ease' }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-4xl font-bold" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

const BREAKDOWN_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  resolution: CheckCircle2,
  critical: AlertTriangle,
  coverage: Users,
  wellness: Heart,
};

const BREAKDOWN_COLORS: Record<string, { text: string; bar: string; bg: { light: string; dark: string } }> = {
  resolution: { text: 'text-emerald-500', bar: 'bg-emerald-500', bg: { light: 'bg-emerald-100', dark: 'bg-emerald-900/40' } },
  critical: { text: 'text-red-500', bar: 'bg-red-500', bg: { light: 'bg-red-100', dark: 'bg-red-900/40' } },
  coverage: { text: 'text-blue-500', bar: 'bg-blue-500', bg: { light: 'bg-blue-100', dark: 'bg-blue-900/40' } },
  wellness: { text: 'text-purple-500', bar: 'bg-purple-500', bg: { light: 'bg-purple-100', dark: 'bg-purple-900/40' } },
};

export function SafetyScoreBreakdown({ dm }: { dm: boolean }) {
  const [data, setData] = useState<SafetyScoreData | null>(null);
  const [loading, setLoading] = useState(true);

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-white' : 'text-slate-800';
  const descCls = dm ? 'text-white' : 'text-slate-500';
  const bodyTextCls = dm ? 'text-white' : 'text-slate-700';
  const mutedCls = dm ? 'text-white' : 'text-slate-400';

  const fetch_ = useCallback(async () => {
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/safety-score`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {/* */} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);


  return (
    <div className="space-y-5">
      <h2 className={`text-lg font-semibold ${titleCls}`}>School Safety Score Breakdown</h2>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          {/* Overall score */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className={`${cardCls} lg:col-span-1`}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-base flex items-center gap-2 ${titleCls}`}>
                  <Shield className="h-4 w-4 text-blue-500" />
                  Overall Safety Score
                </CardTitle>
                <CardDescription className={descCls}>Weighted composite score</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center">
                  <ScoreGauge score={data?.overall ?? 0} size={160} />
                  <div className="flex items-center gap-2 mt-2">
                    {(data?.trend ?? 0) >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`text-sm font-medium ${(data?.trend ?? 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {(data?.trend ?? 0) >= 0 ? '+' : ''}{data?.trend ?? 0} pts
                    </span>
                  </div>
                  <p className={`text-xs mt-1 ${mutedCls}`}>Based on last 30 days</p>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <Card className={`${cardCls} lg:col-span-2`}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-base ${titleCls}`}>Contributing Factors</CardTitle>
                <CardDescription className={descCls}>What's driving your score</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {data && Object.entries(data.breakdown).map(([key, b]) => {
                  const Icon = BREAKDOWN_ICONS[key] || Shield;
                  const c = BREAKDOWN_COLORS[key];
                  return (
                    <div key={key} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${dm ? c.bg.dark : c.bg.light}`}>
                            <Icon className={`h-3.5 w-3.5 ${c.text}`} />
                          </div>
                          <div>
                            <span className={`text-sm font-medium ${bodyTextCls}`}>{b.label}</span>
                            <span className={`text-xs ml-1.5 ${mutedCls}`}>(weight: {b.weight}%)</span>
                          </div>
                        </div>
                        <span className={`text-sm font-bold ${c.text}`}>{b.score}</span>
                      </div>
                      <div className={`h-2 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                        <div className={`h-full rounded-full transition-all duration-700 ${c.bar}`}
                          style={{ width: `${b.score}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: 'Total Alerts (30d)', value: data?.stats.totalAlerts ?? 0, icon: AlertTriangle, color: 'blue' },
              { label: 'Resolved', value: data?.stats.resolvedAlerts ?? 0, icon: CheckCircle2, color: 'emerald' },
              { label: 'Open Critical', value: data?.stats.openCritical ?? 0, icon: AlertTriangle, color: 'red' },
              { label: 'Critical Alerts', value: data?.stats.criticalAlerts ?? 0, icon: Shield, color: 'orange' },
              { label: 'Safe Students', value: data?.stats.safeStudents ?? 0, icon: Users, color: 'emerald' },
              { label: 'Total Students', value: data?.stats.totalStudents ?? 0, icon: Users, color: 'blue' },
            ].map((s, i) => (
              <Card key={i} className={cardCls}>
                <CardContent className="pt-4 pb-3">
                  <p className={`text-xs ${descCls}`}>{s.label}</p>
                  <p className={`text-2xl font-bold mt-1 ${
                    (s.color === 'red' || s.color === 'orange') && s.value > 0 ? `text-${s.color}-500` : titleCls
                  }`}>{s.value.toLocaleString()}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Score interpretation */}
          <Card className={cardCls}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-base ${titleCls}`}>Score Interpretation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { range: '80–100', label: 'Excellent', desc: 'School safety is well-managed. Continue current practices.', color: 'emerald' },
                  { range: '60–79', label: 'Moderate', desc: 'Some areas need attention. Review open incidents and wellness scores.', color: 'amber' },
                  { range: '0–59', label: 'Needs Attention', desc: 'Immediate action required. Focus on resolving critical alerts.', color: 'red' },
                ].map((r, i) => (
                  <div key={i} className={`rounded-lg p-3 border ${dm ? 'border-slate-700 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold text-${r.color}-500`}>{r.range}</span>
                      <span className={`text-xs font-semibold text-${r.color}-500`}>{r.label}</span>
                    </div>
                    <p className={`text-xs ${descCls}`}>{r.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
